/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Switch } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { paragraphXSmall, weightBold, weightSemiBold } from 'src/components/css'

const translate = t as (key: string, options?: unknown) => string

interface RegistrationStripProps {
  isRegistrationOn: boolean
  canEdit: boolean
  onToggle: (next: boolean) => void
}

/**
 * One slim line replacing the old tall amber banner, so the tree keeps its
 * vertical space. Only shown while registration is actually on.
 */
const RegistrationStrip = ({
  isRegistrationOn,
  canEdit,
  onToggle,
}: RegistrationStripProps) => {
  'use memo'

  if (!isRegistrationOn) {
    return null
  }

  return (
    <div css={stripStyle} role='status'>
      <span css={dotStyle} aria-hidden />
      <span>
        {/* The lead carries its own period — it is a full sentence, not a
            label, so no punctuation is appended here. */}
        <strong css={strongStyle}>{translate('Web:registrationOnLead')}</strong>{' '}
        {translate('Web:registrationStructureWarning')}
      </span>
      <span css={spacerStyle} />
      {canEdit && (
        <label css={toggleStyle}>
          <span css={toggleLabelStyle}>
            {translate('Web:registrationOnToggleLabel')}
          </span>
          {/* Labelled by the text beside it, so no aria-label — one would
              override the visible label for a screen reader. */}
          <Switch size='small' checked={isRegistrationOn} onChange={onToggle} />
        </label>
      )}
    </div>
  )
}

const stripStyle = css`
  ${paragraphXSmall}
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 8px 16px;
  background: rgba(255, 193, 7, 0.09);
  border-bottom: 1px solid ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_50};
`

const dotStyle = css`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${colors.WARNING_300};
  flex: none;
`

const strongStyle = css`
  ${weightSemiBold}
  color: ${colors.NEUTRAL_0};
`

const spacerStyle = css`
  flex: 1;
`

const toggleLabelStyle = css`
  ${weightBold}
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
`

const toggleStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
  cursor: pointer;
`

export default RegistrationStrip
